import { createFileRoute } from '@tanstack/react-router'
import { CameraIcon } from 'lucide-react'
import { useState } from 'react'

import { Blueprint } from '@/components/blueprint'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { CameraScanner } from '@/features/scan/CameraScanner'
import { ScanResultSheet } from '@/features/scan/ScanResultSheet'
import { useShelfScan } from '@/features/shelf/useShelfScan'

export const Route = createFileRoute('/shelf/scan')({ component: ShelfScan })

/**
 * The shelf's Scan tab (prototype 2c): a viewfinder, and the code under the
 * label for when the camera will not read it. The camera starts only when
 * asked, so opening the tab never throws a permission prompt; a hand scanner
 * works on every shelf screen without it.
 */
function ShelfScan() {
  const scan = useShelfScan()
  const [camera, setCamera] = useState(false)
  const [code, setCode] = useState('')

  return (
    <div className="flex flex-1 flex-col">
      <Blueprint className="bg-steel-900 text-steel-300 mx-5 mt-2 flex min-h-[380px] flex-1 flex-col items-center justify-center gap-3">
        {camera ? (
          <div className="w-full self-stretch p-2">
            <CameraScanner onCode={(found) => void scan.handle(found)} />
          </div>
        ) : (
          <>
            <div
              aria-hidden="true"
              className="border-steel-400 relative size-[200px] border border-dashed"
            >
              <div className="bg-steel-400 absolute inset-x-0 top-1/2 h-px" />
            </div>
            <p className="font-heading text-[15px] tracking-[0.06em] uppercase">Point at a label</p>
            <Button
              variant="secondary"
              className="text-ground border-steel-400"
              onClick={() => setCamera(true)}
            >
              <CameraIcon />
              Start the camera
            </Button>
          </>
        )}
      </Blueprint>

      <form
        className="flex gap-2 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          const trimmed = code.trim()
          if (!trimmed) return
          setCode('')
          void scan.handle(trimmed)
        }}
      >
        <Input
          aria-label="Code"
          value={code}
          autoComplete="off"
          placeholder="or type the code under it"
          onChange={(event) => setCode(event.target.value)}
          className="h-12 text-base"
        />
        <Button type="submit" className="h-12 px-4" disabled={scan.pending}>
          {scan.pending && <Spinner className="size-4" />}
          Look up
        </Button>
      </form>

      {scan.error && (
        <Alert variant="destructive" className="mx-5 w-auto">
          <AlertDescription>{scan.error}</AlertDescription>
        </Alert>
      )}
      {scan.sheet && <ScanResultSheet result={scan.sheet} onClose={scan.closeSheet} />}
    </div>
  )
}
